import {
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  sortByDateTime,
  Spacer,
  Tag,
  usePersistMuiTableState,
  useUrlTableFilter,
  uppercaseFirstLetter,
  dateFormatterDayMonthYearLong2Digits,
  MonthPeriod,
  getMonthPeriodFromUrlParam,
  getPreviousAndNextIds,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  selectPlanningsByDayList,
  selectPlanningsByDayStatus,
  selectTripsByDayList,
  selectTripsByDayStatus,
} from "../trips/tripsSlice";
import {
  TripsAndPlanningsHistoryDisplayed,
  TripsAndPlanningsHistory,
} from "../trips/tripsApi";
import {
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import styled from "styled-components";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  formatDate,
  formatDateHourMinute,
  formatHour,
  formatMileage,
  formatPercent,
  getDate,
  getDateTime,
} from "../../../../libSlice/materialUiFormats";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { getTripsAndPlanningsHistory } from "../trips/tripLib";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { getFilteredDataGridRows } from "@qivia/ui/src/designSystem/components/materialUiTable/getFilteredDataGridRows";
import "../../../../i18n/i18n";
import { useParams, useSearchParams } from "react-router-dom";
import { endOfMonth, startOfMonth } from "date-fns";

interface SharedInterfaceHistoryProps {
  hasNoAdvancedAnalyticsVehicles: boolean;
  loadTransactionByPeriodAction: (payload: {
    from: string;
    to: string;
  }) => void;
  loadAuthorizationsRejectedByPeriodAction: (payload: {
    from: string;
    to: string;
  }) => void;
  sidePanelAction?: (props: { currentTripId: string }) => void;
  sidePanel?: (props: {
    trip: TripsAndPlanningsHistory;
    previousTripId: string | null;
    nextTripId: string | null;
  }) => JSX.Element;
  loadPlanningsByPeriodAction: (payload: { from: string; to: string }) => void;
  loadTripsByPeriodAction: (payload: { from: string; to: string }) => void;
}

type WithId = TripsAndPlanningsHistory & { id: string };

export const SharedInterfaceHistory = (props: SharedInterfaceHistoryProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const driverSearchParam = searchParams.get("driver");
  const monthSearchParam = searchParams.get("month");

  const sidePanelAction = props.sidePanelAction;
  const tripsListInit = useAppSelector(selectTripsByDayList);
  const tripsListStatus = useAppSelector(selectTripsByDayStatus);
  const planningsListInit = useAppSelector(selectPlanningsByDayList);
  const planningsListStatus = useAppSelector(selectPlanningsByDayStatus);

  const [listDisplayed, setListDisplayed] = useState<
    TripsAndPlanningsHistoryDisplayed[] | null
  >(null);
  const [currentTripDetailsId, setCurrentTripDetailsUuid] = useState<
    string | null
  >(null);

  const [selectedMonth, setSelectedMonth] = useState<MonthPeriod | undefined>(
    getMonthPeriodFromUrlParam(monthSearchParam),
  );

  const {
    loadTransactionByPeriodAction,
    loadAuthorizationsRejectedByPeriodAction,
    loadPlanningsByPeriodAction,
    loadTripsByPeriodAction,
  } = props;

  const pageName = "trips.history";

  const fromNavigate = searchParams.get("fromNavigate") === "true";

  const hasExecutedFromNavigate = useRef(false);
  const shouldExecuteFromNavigate = useRef(false);

  if (fromNavigate && !shouldExecuteFromNavigate.current) {
    shouldExecuteFromNavigate.current = true;
  }

  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      reference: "size",
      registrationNumber: "size",
      driverName: "size",
      count: "avg",
      totalDistanceInKm: "sum",
      hoursDuration: "avg",
      distanceVariation: "sum",
      distanceVariationPercent: "avg",
      driveTimeVariation: "avg",
      driveTimeVariationPercent: "avg",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (
      key: keyof Partial<TripsAndPlanningsHistoryDisplayed>,
      value: string | null,
    ) => {
      switch (key) {
        case "date":
          return dateFormatterDayMonthYearLong2Digits(
            new Date(value as string),
          );
        case "usageStartDate":
        case "usageEndDate":
          return dateFormatterDayMonthLongYearAndHourMinute2Digits(
            new Date(value as string),
          );
        default:
          return value;
      }
    },
    [],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "date",
        "usageStartDate",
        "usageEndDate",
      ] as (keyof TripsAndPlanningsHistoryDisplayed)[],
    [],
  );

  const tripsAndPlanningsHistoryList: TripsAndPlanningsHistory[] =
    useMemo(() => {
      const tripsAndPlanningsHistoryInit = getTripsAndPlanningsHistory({
        trips: tripsListInit,
        plannings: planningsListInit,
      });
      const rowsWithId = tripsAndPlanningsHistoryInit.map((vt) => {
        let newDict: TripsAndPlanningsHistory & { id: string } = {
          ...vt,
          id:
            vt.vehicleUuid +
            vt.usageStartDate?.toString().split("T")[0] +
            vt.driverUuid,
        };
        Object.entries(vt).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(
              key as keyof TripsAndPlanningsHistoryDisplayed,
            )
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof TripsAndPlanningsHistoryDisplayed,
                    value as string,
                  )
                : "",
            };
          }
        });
        return newDict;
      });

      const sortedRows = sortByDateTime(rowsWithId, "desc", "createdAt");

      if (!driverSearchParam) {
        return sortedRows;
      }

      return sortedRows.filter((trip) => trip.driverUuid === driverSearchParam);
    }, [
      driverSearchParam,
      findKeysToTranslate,
      keysToTranslate,
      planningsListInit,
      tripsListInit,
    ]);

  const tripsDetailsExcelColumns = useMemo(
    () => [
      "registrationNumber",
      "reference",
      "vehicleModel",
      "driver",
      "analyticCode1",
      "date",
      "startTime",
      "endTime",
      "duration",
      "distance",
      "vehicleAnalyticCode",
    ],
    [],
  );

  const downloadTripsDetails = useCallback(() => {
    const visibleRows = getFilteredDataGridRows(apiRef);

    const headerTitles = tripsDetailsExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`trips.details.export.column.${key}.title`)),
    );

    const bodyList: (string | number)[][] = (
      visibleRows as TripsAndPlanningsHistory[]
    ).flatMap((ld) =>
      ld.trips.map((trip) => [
        ld.registrationNumber ?? "",
        ld.reference ?? "",
        ld.vehicleModel ?? "",
        ld.driverName ?? "",
        ld.driverAnalyticCode1 ?? "",
        ld.date,
        formatDateHourMinute(new Date(trip.startDate)),
        formatDateHourMinute(new Date(trip.endDate)),
        trip.secondsDuration ? Math.round(trip.secondsDuration / 60) : "",
        trip.distance ?? "",
        ld.vehicleAnalyticCode ?? "",
      ]),
    );

    const file = createFileXlsx({
      tabTitle: t(`${pageName}.view.title`),
      headerTitles,
      body: bodyList,
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName: "QIVIA_" + t("trips.details.excel.title") + ".xlsx",
      }),
    );
  }, [apiRef, dispatch, t, tripsDetailsExcelColumns]);

  //initialisation
  useEffect(() => {
    if (!selectedMonth) {
      setSelectedMonth([
        startOfMonth(new Date()).toISOString(),
        endOfMonth(new Date()).toISOString(),
      ]);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedMonth) {
      loadPlanningsByPeriodAction({
        from: selectedMonth[0],
        to: selectedMonth[1],
      });
      loadTripsByPeriodAction({
        from: selectedMonth[0],
        to: selectedMonth[1],
      });
    }
  }, [loadPlanningsByPeriodAction, loadTripsByPeriodAction, selectedMonth]);

  useEffect(() => {
    if (
      tripsListInit &&
      tripsListStatus === "success" &&
      planningsListInit &&
      planningsListStatus === "success"
    ) {
      const tripsAndPlanningsHistorySorted = sortByDateTime(
        tripsAndPlanningsHistoryList,
        "desc",
        "date",
      );
      setListDisplayed(tripsAndPlanningsHistorySorted);
    }
  }, [
    planningsListInit,
    planningsListStatus,
    tripsAndPlanningsHistoryList,
    tripsListInit,
    tripsListStatus,
  ]);

  const resolveDriverName = useCallback(
    (uuid: string) =>
      tripsAndPlanningsHistoryList.find((trip) => trip.driverUuid === uuid)
        ?.driverName,
    [tripsAndPlanningsHistoryList],
  );

  useUrlTableFilter(apiRef, !!listDisplayed, {
    paramName: "driver",
    field: "driverName",
    filterId: "driverUrlFilter",
    resolveValue: resolveDriverName,
  });

  useEffect(() => {
    if (props.hasNoAdvancedAnalyticsVehicles) {
      setListDisplayed([]);
    }
  }, [props.hasNoAdvancedAnalyticsVehicles]);

  useEffect(() => {
    if (params.tab !== "history" || !params.id) {
      return;
    }
    setCurrentTripDetailsUuid(params.id);
  }, [params.tab, params.id]);

  const currentTripDetails: TripsAndPlanningsHistory | null = useMemo(
    () =>
      tripsAndPlanningsHistoryList.find(
        (t) => "id" in t && t.id === currentTripDetailsId,
      ) ?? null,
    [currentTripDetailsId, tripsAndPlanningsHistoryList],
  );

  // const transactionsAndAuthFilteredOnTripDate = useMemo(() => {
  //   if (!currentTripDetails) return [];

  //   return [
  //     ...(transactionsListByPeriodInit ?? []),
  //     ...(authorizationsRejectedByByPeriodListInit ?? []),
  //   ].filter((t) => t.driver === currentTripDetails.driverUuid);
  // }, [
  //   currentTripDetails,
  //   transactionsListByPeriodInit,
  //   authorizationsRejectedByByPeriodListInit,
  // ]);

  const searchBarProps = {
    values: tripsAndPlanningsHistoryList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "vehicleUuid" as const,
      "driverUuid" as const,
      "trips" as const,
      "maxOdometerValue" as const,
      "minOdometerValue" as const,
      "distanceVariationPercent" as const,
      "driveTimeVariationPercent" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("trips.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const navigationId = useMemo(() => {
    const visibleRows = getFilteredDataGridRows(apiRef) as WithId[];
    const list =
      visibleRows.length > 0
        ? visibleRows
        : ((listDisplayed ?? []) as WithId[]);

    const { previousId, nextId } = getPreviousAndNextIds(
      list,
      currentTripDetailsId,
    );

    return { previousTripId: previousId, nextTripId: nextId };
  }, [apiRef, currentTripDetailsId, listDisplayed]);

  useEffect(() => {
    if (currentTripDetails) {
      const dateString = currentTripDetails.date;
      const [day, month, year] = dateString.split("/").map(Number);
      const date = new Date(year, month - 1, day);
      const startDate = new Date(year, month - 1, day);
      const nextDate = new Date(startDate.setDate(date.getDate() + 1));
      loadTransactionByPeriodAction({
        from: date.toISOString(),
        to: nextDate.toISOString(),
      });
      loadAuthorizationsRejectedByPeriodAction({
        from: date.toISOString(),
        to: nextDate.toISOString(),
      });
    }
  }, [
    currentTripDetails,
    loadTransactionByPeriodAction,
    loadAuthorizationsRejectedByPeriodAction,
  ]);

  const openDetailsAction = useCallback(
    (params: Partial<TripsAndPlanningsHistory & { id: string }>) => {
      sidePanelAction &&
        sidePanelAction({ currentTripId: params["id"] as string });

      // if (currentTripDetailsId === params["id"]) {
      //   setCurrentTripDetailsUuid(null);
      // } else {
      //   if (!params.date) return;
      //   setCurrentTripDetailsUuid(params["id"] as string);
      //   const dateString = params.date;
      //   const [day, month, year] = dateString.split("/").map(Number);
      //   const date = new Date(year, month - 1, day);
      //   const startDate = new Date(year, month - 1, day);
      //   const nextDate = new Date(startDate.setDate(date.getDate() + 1));
      //   loadTransactionByPeriodAction({
      //     from: date.toISOString(),
      //     to: nextDate.toISOString(),
      //   });
      //   loadAuthorizationsRejectedByPeriodAction({
      //     from: date.toISOString(),
      //     to: nextDate.toISOString(),
      //   });
      // }
    },
    [sidePanelAction],
  );

  // Only after a navigate from an other page like transaction, we select the row to send to the top of the MaterialUiTable
  useEffect(() => {
    if (
      listDisplayed &&
      shouldExecuteFromNavigate.current &&
      !hasExecutedFromNavigate.current
    ) {
      hasExecutedFromNavigate.current = true;
      shouldExecuteFromNavigate.current = false;
    }
  }, [listDisplayed, apiRef]);

  const columnsDisplayed: {
    [index in keyof TripsAndPlanningsHistoryDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      date: {
        type: "date",
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      reference: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value && (
            <StyledDivImg>
              <StyledImg src={Car} alt="Car" />
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledDivImg>
          ),
      },
      registrationNumber: {
        type: "string",
      },
      driverName: {
        type: "string",
        width: 200,
      },
      count: {
        type: "number",
      },
      totalDistanceInKm: {
        type: "number",
        valueFormatter: formatMileage,
      },
      distanceVariation: {
        width: 230,
        type: "number",
        valueFormatter: formatMileage,
      },
      distanceVariationPercent: {
        width: 220,
        type: "number",
        valueFormatter: formatPercent,
      },
      usageStartDate: {
        type: "date",
        width: 250,
        valueGetter: getDateTime,
        valueFormatter: formatDateHourMinute,
      },
      usageEndDate: {
        type: "date",
        width: 220,
        valueGetter: getDateTime,
        valueFormatter: formatDateHourMinute,
      },
      hoursDuration: {
        type: "number",
        valueFormatter: formatHour,
        renderCell: (p) => {
          return p.id !== GRID_AGGREGATION_ROOT_FOOTER_ROW_ID ? (
            <>{p.formattedValue as string}</>
          ) : (
            <StyledColumn>
              <StyledValue>{formatHour(p.value as number)}</StyledValue>
              <StyledLabel>Moyenne</StyledLabel>
            </StyledColumn>
          );
        },
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
    };
  }, []);

  if (
    !listDisplayed ||
    tripsListStatus === "processing" ||
    planningsListStatus === "processing"
  )
    return <LaunchPage hasBorderRadius={true} />;

  return (
    <StyledContainer>
      <MaterialUiTable<
        keyof TripsAndPlanningsHistoryDisplayed,
        TripsAndPlanningsHistoryDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        downloadDetailsAction={downloadTripsDetails}
        onRowClickAction={openDetailsAction}
        rowToSendToTop={currentTripDetailsId}
        monthPicker={{
          monthFilterCallBack: setSelectedMonth,
          monthFilterValue: selectedMonth,
        }}
      />
      {currentTripDetails && (
        <>
          <Spacer y={2} />
          {/* <StyledBottom>
            <DayTripDetails
              date={getDate(currentTripDetails.date)}
              vehicle={{
                registrationNumber: currentTripDetails.registrationNumber,
                reference: currentTripDetails.reference,
                model: currentTripDetails.vehicleModel,
                vehicleType: currentTripDetails.vehicleType,
              }}
              totalDistance={currentTripDetails.totalDistanceInKm ?? 0}
              trips={currentTripDetails.trips}
              plannings={currentTripDetails.associatedPlanning?.trips ?? []}
              onClose={() => setCurrentTripDetailsUuid(null)}
              driver={{
                name: currentTripDetails.driverName,
                city: currentTripDetails.driverCity,
              }}
              planningTotalDistance={
                currentTripDetails.associatedPlanning?.expectedMileagePerDay ??
                0
              }
              transactionsAndAuth={transactionsAndAuthFilteredOnTripDate}
            />
          </StyledBottom> */}
        </>
      )}
      {props.sidePanel && currentTripDetails ? (
        props.sidePanel({
          trip: currentTripDetails,
          previousTripId: navigationId.previousTripId,
          nextTripId: navigationId.nextTripId,
        })
      ) : (
        <></>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

// const StyledBottom = styled.div`
//   display: flex;
//   height: 50%;
//   width: 100%;
// `;

const StyledDivImg = styled.div`
  display: flex;
  align-items: center;
`;

const StyledImg = styled.img`
  filter: grayscale(100%);
  width: 2rem;
  height: 60px;
  object-fit: contain;
  padding-bottom: 2.5px;
`;

const StyledLabel = styled.div`
  color: ${colors["colors/text/darkGrey"]};
  ${typographies["Body/XXS"]};
  height: 2rem;
`;

const StyledValue = styled.div`
  height: 2.2rem;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;
